import { IndexKey, getIndices, sortByIndex } from "@tldraw/utils"
import { T } from "@tldraw/validate"
import {
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  DefaultDashStyle,
  DefaultSizeStyle,
  RecordProps,
  StyleProp,
  TLBaseShape,
  TLDefaultDashStyle,
  TLDefaultSizeStyle,
} from "tldraw"
import { objectMapFromEntries } from "../client/upstream/object"
import { Color, Expr } from "./Expr"

/** @public */
export const LineShapeSplineStyle = StyleProp.defineEnum("tldraw:spline2", {
  defaultValue: "line",
  values: ["cubic", "line"],
})

/** @public */
export type LDLineShapeSplineStyle = T.TypeOf<typeof LineShapeSplineStyle>

/** @public */
export interface LDLineShapePoint {
  id: string
  index: IndexKey
  x: number
  y: number
}

const lineShapePointValidator: T.ObjectValidator<LDLineShapePoint> = T.object({
  id: T.string,
  index: T.indexKey,
  x: T.number,
  y: T.number,
})

/** @public */
export interface LDLineShapeProps {
  dash: TLDefaultDashStyle
  spline: LDLineShapeSplineStyle
  points: Record<string, LDLineShapePoint>
  scale: number
  layer: string
  parameterValues: {
    strokeColor: Expr<Color>
    strokeWidth: Expr<number>
  }
}

/** @public */
export type LDLineShape = TLBaseShape<"custom-line", LDLineShapeProps>

/** @public */
export const lineShapeProps: RecordProps<LDLineShape> = {
  dash: DefaultDashStyle,
  spline: LineShapeSplineStyle,
  points: T.dict(T.string, lineShapePointValidator),
  scale: T.nonZeroNumber,
  layer: T.string,
  parameterValues: T.any,
}

/** @public */
export const lineShapeVersions = createShapePropsMigrationIds("custom-line", {
  AddSnapHandles: 1,
  RemoveExtraHandleProps: 2,
  HandlesToPoints: 3,
  PointIndexIds: 4,
  AddScale: 5,
})

/** @public */
export const lineShapeMigrations = createShapePropsMigrationSequence({
  sequence: [
    {
      id: lineShapeVersions.AddSnapHandles,
      up: (props) => {
        for (const handle of Object.values(props.handles)) {
          ;(handle as any).canSnap = true
        }
      },
      down: "retired",
    },
    {
      id: lineShapeVersions.RemoveExtraHandleProps,
      up: (props) => {
        props.handles = objectMapFromEntries(
          Object.values(props.handles).map((handle: any) => [
            handle.index,
            {
              x: handle.x,
              y: handle.y,
            },
          ]),
        )
      },
      down: (props) => {
        const handles = Object.entries(props.handles)
          .map(([index, handle]: any) => ({ index, ...handle }))
          .sort(sortByIndex)
        props.handles = Object.fromEntries(
          handles.map((handle, i) => {
            const id =
              i === 0
                ? "start"
                : i === handles.length - 1
                  ? "end"
                  : `handle:${handle.index}`
            return [
              id,
              {
                id,
                type: "vertex",
                canBind: false,
                canSnap: true,
                index: handle.index,
                x: handle.x,
                y: handle.y,
              },
            ]
          }),
        )
      },
    },
    {
      id: lineShapeVersions.HandlesToPoints,
      up: (props) => {
        const sortedHandles = (
          Object.entries(props.handles) as [
            IndexKey,
            { x: number; y: number },
          ][]
        )
          .map(([index, { x, y }]) => ({ x, y, index }))
          .sort(sortByIndex)

        props.points = sortedHandles.map(({ x, y }) => ({ x, y }))
        delete props.handles
      },
      down: (props) => {
        const indices = getIndices(props.points.length)

        props.handles = Object.fromEntries(
          props.points.map((handle: { x: number; y: number }, i: number) => {
            const index = indices[i]
            return [
              index,
              {
                x: handle.x,
                y: handle.y,
              },
            ]
          }),
        )

        delete props.points
      },
    },
    {
      id: lineShapeVersions.PointIndexIds,
      up: (props) => {
        const indices = getIndices(props.points.length)

        props.points = Object.fromEntries(
          props.points.map((point: { x: number; y: number }, i: number) => {
            const id = indices[i]
            return [
              id,
              {
                id: id,
                index: id,
                x: point.x,
                y: point.y,
              },
            ]
          }),
        )
      },
      down: (props) => {
        const sortedHandles = (
          Object.values(props.points) as {
            x: number
            y: number
            index: IndexKey
          }[]
        ).sort(sortByIndex)

        props.points = sortedHandles.map(({ x, y }) => ({ x, y }))
      },
    },
    {
      id: lineShapeVersions.AddScale,
      up: (props) => {
        props.scale = 1
      },
      down: (props) => {
        delete props.scale
      },
    },
  ],
})
